import { Theme } from "@mui/material";
import { ColDef } from "ag-grid-community";
import { TimeframeTypeEnum } from "../../../common/enums/TimeframeTypeEnum";
import { UIViewEnum } from "../../../common/enums/UIViewEnum";
import { IBaseGridColumnDefinition } from "../../../common/models/BaseGridColumnDefinition";
import { getTagRoute } from "../../../common/utilities/RouteHelpers";
import { getTimeframeTimeDisplayText } from "../../../common/utilities/TimeframeUtilities";
import { RowDef } from "../../../components/ag-grid-extensions/models/RowDef";
import { StandardCellData } from "../../../components/ag-grid-extensions/renderers/StandardCellRenderer";
import { createNumericColumn, createTextColumn, createTimestampColumn } from "../../../components/summary-listing-grid/ColumnCreators";
import { ICompressorLynxGridModel, ICompressorLynxGridRow } from "../../../services/summary-listing-data-service/CompressorLynxSummaryModels";

export type CompressorLynxRow = RowDef & {
    deviceID: number;
    tagID: number;
    cell0: StandardCellData;
    cell1: StandardCellData;
    cell2: StandardCellData;
    cell3: StandardCellData;
    cell4: StandardCellData;
    cell5: StandardCellData;
    cell6: StandardCellData;
    cell7: StandardCellData;
    cell8: StandardCellData;
};

export function createGridData(sourceData: ICompressorLynxGridModel | undefined, groupID: number, theme: Theme) {
    const cols: Array<ColDef> = createGridColumns(sourceData, theme);
    const rows: Array<RowDef> = createGridRows(sourceData, groupID);
    const data = cols && rows
        ? {
            rows: rows,
            cols: cols
        } : {
            rows: new Array<RowDef>(),
            cols: new Array<ColDef>()
        };
    return data;
}

export function createGridRows(sourceData: ICompressorLynxGridModel | undefined, groupID: number): Array<CompressorLynxRow> {
    return sourceData?.rows.map((row: ICompressorLynxGridRow) => {
        return {
            deviceID: Number(row.deviceID),
            tagID: Number(row.tagID),
            // Device Name
            cell0: {
                displayText: row.deviceName,
                // TODO: this has to be changed to redirect                
                href: getTagRoute(UIViewEnum.Tag_Details, groupID, undefined, Number(row.deviceID), undefined, undefined, Number(row.tagID))
            },
            // Tag Name
            cell1: {
                displayText: row.tagName,
            },
            // Location
            cell2: {
                displayText: row.location,
            },
            // Status
            cell3: {
                displayText: row.status.displayText,
                dataValue: row.status.dataValue
            },
            // Runtime % (Last 7 Days)
            cell4: {
                displayText: row.lastSevenDaysRuntimePercent.displayText,
                dataValue: row.lastSevenDaysRuntimePercent.dataValue
            },
            // Runtime % (Month to Date)
            cell5: {
                displayText: row.currentMonthRuntimePercent.displayText,
                dataValue: row.currentMonthRuntimePercent.dataValue
            },
            // Downtime Hrs (Current Month)
            cell6: {
                displayText: row.currentMonthDownHours.displayText,
                dataValue: row.currentMonthDownHours.dataValue
            },
            // Uptime Hrs (Current Month
            cell7: {
                displayText: row.currentMonthUpHours.displayText,
                dataValue: row.currentMonthUpHours.dataValue
            },
            // Last Update
            cell8: {
                displayText: getTimeframeTimeDisplayText(TimeframeTypeEnum.None, row.lastUpdate ?? ""),
                timestamp: typeof row.lastUpdate === "string" ? new Date(row.lastUpdate) : row.lastUpdate
            }
        };

    }) ?? new Array<CompressorLynxRow>();
}

export function createGridColumns(sourceData: ICompressorLynxGridModel | undefined, theme: Theme): Array<ColDef> {

    function CompressorLynxStatusCellStyler(params: any): any {
        const cell = params.colDef.cellRendererParams.cellDataGetter(params);

        const style: { [key: string]: string; } = {};
        if (cell?.dataValue === 1) {
            style.color = theme.palette.error.main;
            style.fontWeight = "bold";
        } else {
            style.color = theme.palette.success.main;
        }

        return style;
    }

    return sourceData?.columns.map((col: IBaseGridColumnDefinition, index: number) => {

        switch (index) {
            case 0: // Device Name
                return createTextColumn({ field: `cell${index}`, headerName: col.headerText, initialSort: "asc", initialSortIndex: 0, footerAggregate: col.aggregate });
            case 1: // Tag Name
                return createTextColumn({ field: `cell${index}`, headerName: col.headerText, initialSort: "asc", initialSortIndex: 1 });
            case 2: // Location
                return createTextColumn({ field: `cell${index}`, headerName: col.headerText });
            case 3: // Status
                return createTextColumn({ field: `cell${index}`, headerName: col.headerText, styler: CompressorLynxStatusCellStyler });
            case 4: // Runtime % (Last 7 Days)
                return createNumericColumn({ field: `cell${index}`, headerName: col.headerText, footerAggregate: col.aggregate });
            case 5: // Runtime % (Month to Date)
                return createNumericColumn({ field: `cell${index}`, headerName: col.headerText, footerAggregate: col.aggregate });
            case 6: // Downtime Hrs (Current Month)
                return createNumericColumn({ field: `cell${index}`, headerName: col.headerText, footerAggregate: col.aggregate });
            case 7: // Uptime Hrs (Current Month
                return createNumericColumn({ field: `cell${index}`, headerName: col.headerText, footerAggregate: col.aggregate });
            case 8: // Last Update
                return createTimestampColumn({ field: `cell${index}`, headerName: col.headerText });
            default:
                return createTextColumn({ field: `cell${index}`, headerName: col.headerText });
        }
    }) ?? new Array<ColDef>();
}
